import classNames from "classnames";
import { CSSProperties, useCallback } from "react";
import { Button, ButtonGroup } from "react-bootstrap";

import { BomNode } from "@/features/job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { useTheme } from "@/stores/ThemeContext";
import { UpdatePurchasingPriorityMutation } from "../job-tracking/queries/tracking-data/useUpdatePurchasingPriorityMutation";
import { priorityOptionColors, PriorityOption, priorityOptions } from "./purchasing-priority-utils";

interface PurchasingPriorityButtonGroupProps {
  bomNode: BomNode;
  updatePurchasingPriorityMutation: UpdatePurchasingPriorityMutation;
}

/**
 * A component that renders a button group for selecting the purchasing priority level for a BOM hierarchy node.
 */
export const PurchasingPriorityButtonGroup: React.FC<PurchasingPriorityButtonGroupProps> = ({
  bomNode,
  updatePurchasingPriorityMutation,
}) => {
  return (
    <>
      <ButtonGroup size="sm" className="ms-2">
        {priorityOptions.map((buttonPriority) => (
          <PurchasingPriorityButton
            key={buttonPriority}
            bomNode={bomNode}
            buttonPriority={buttonPriority}
            updatePurchasingPriorityMutation={updatePurchasingPriorityMutation}
          />
        ))}
      </ButtonGroup>
    </>
  );
};

const PurchasingPriorityButton: React.FC<{
  bomNode: BomNode;
  buttonPriority: PriorityOption;
  updatePurchasingPriorityMutation: UpdatePurchasingPriorityMutation;
}> = ({ bomNode, buttonPriority, updatePurchasingPriorityMutation }) => {
  const { oppositeTheme } = useTheme();

  // Define the click callback for the button that adds or updates a job-specific purchasing priority for the current node
  const handleClick = useCallback(() => {
    updatePurchasingPriorityMutation.mutate({
      action: "upsert",
      upsertDetails: [
        {
          jobSpecific: true,
          path: bomNode.path,
          priority: buttonPriority,
          recordToUpdate: bomNode.purchasingPriority.nodeJobRecord,
        },
      ],
    });
  }, [updatePurchasingPriorityMutation, bomNode.path, bomNode.purchasingPriority, buttonPriority]);

  // Compute the style to apply based on the active state and priority
  const active = buttonPriority === bomNode.purchasingPriority.priority;
  const nodePriority = bomNode.purchasingPriority.priority;
  const style: CSSProperties | undefined =
    active ?
      {
        borderColor: priorityOptionColors[nodePriority],
        backgroundColor: priorityOptionColors[nodePriority],
        color: "rgba(var(--bs-dark-rgb), var(--bs-text-opacity))",
        opacity: "var(--bs-text-opacity)",
      }
    : undefined;

  return (
    <Button
      key={buttonPriority}
      style={style}
      className={classNames("py-0 btn-inherit-opacity", { "fw-bold": active })}
      variant={active ? oppositeTheme : `outline-${oppositeTheme}`}
      active={active}
      disabled={
        bomNode.purchasingPriority.appliedRecordSource !== "job" || // Editing is disabled unless node is job-specific
        active || // Active button cannot be clicked again
        updatePurchasingPriorityMutation.isPending // Disable while mutation is in-progress
      }
      onClick={handleClick}
    >
      {buttonPriority}
    </Button>
  );
};
